import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { DoulaApplication } from "@/types/doulaApplication";

import {
	getMonthsArray,
	formatStringToDate as formatDate,
	formatCurrency
} from "@/utils/helpers"

import { motion } from "framer-motion";
import {
	User,
	MapPin,
	Calendar,
	Package,
	Award,
	Edit,
	CheckCircle,
	XCircle,
	Settings,
	Clock,
	FileText,
	ArrowLeft,
	Save,
	Shield,
	Star,
	Mail,
	Eye,
	Ban,
	Play,
	CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

import styles from './Admin.module.css';

export default function DoulaApplicationDetail() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [application, setApplication] = useState<DoulaApplication | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [activeTab, setActiveTab] = useState("profile");
	const [adminNotes, setAdminNotes] = useState("");

	// Profile form state for manual save
	const [profileForm, setProfileForm] = useState({
		title: "",
		bio: "",
		years_experience: 0,
		births_attended_range: "",
		location_address: "",
		location_city: "",
		location_state: "",
		location_zip: "",
	});
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	// Edit states
	const [editingPackage, setEditingPackage] = useState<any>(null);

	const [editingCertification, setEditingCertification] = useState<any>(null);
	const [editPackageDialogOpen, setEditPackageDialogOpen] = useState(false);

	const [editCertificationDialogOpen, setEditCertificationDialogOpen] =
		useState(false);

	useEffect(() => {
		if (id) {
			loadApplication();
		}
	}, [id]);

	const loadApplication = async () => {
		if (!id) return;

		try {
			setLoading(true);

			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the get-doula-profile edge function to get all data
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/get-doula-profile/${id}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to load application");
			}

			if (!result.data) {
				throw new Error("No application data found");
			}

			// The get-doula-profile edge function now returns all the data we need for admin access
			const doulaData = result.data;

			// Map the profile data structure to match our interface
			const processedData = {
				id: doulaData.id,
				user_id: doulaData.user_id,
				status: doulaData.status,
				title: doulaData.title,
				bio: doulaData.bio,
				years_experience: doulaData.years_experience || 0,
				births_attended_range: doulaData.births_attended_range || "Unknown",
				location_city: doulaData.location_city,
				location_state: doulaData.location_state,
				location_address: doulaData.location_address,
				location_zip: doulaData.location_zip,
				hourly_rate: null, // Not included in current data
				booking_window_months: 6, // Default
				created_at: doulaData.created_at,
				updated_at: doulaData.updated_at,
				availability_notes: null, // Not included in current data
				profile_photo_url: doulaData.profile_photo_url,
				intro_video_url: doulaData.intro_video_url,
				tags: doulaData.tags || [],
				personality_traits: doulaData.personality_traits || [],
				services_offered: doulaData.services_offered || [],
				is_verified: doulaData.is_verified,
				admin_notes: doulaData.admin_notes,
				months_available: doulaData.months_available || {
					january: true,
					february: true,
					march: true,
					april: true,
					may: true,
					june: true,
					july: true,
					august: true,
					september: true,
					october: true,
					november: true,
					december: true,
				},
				users: doulaData.users,
				packages: doulaData.packages || [],
				experience: doulaData.experience || [],
				certifications: doulaData.certifications || [],
			};

			setApplication(processedData);
			setAdminNotes(processedData.admin_notes || "");

			// Initialize profile form with loaded data
			setProfileForm({
				title: processedData.title || "",
				bio: processedData.bio || "",
				years_experience: processedData.years_experience || 0,
				births_attended_range: processedData.births_attended_range || "",
				location_address: processedData.location_address || "",
				location_city: processedData.location_city || "",
				location_state: processedData.location_state || "",
				location_zip: processedData.location_zip || "",
			});
			setHasUnsavedChanges(false);
		} catch (error) {
			// Handle error silently
		} finally {
			setLoading(false);
		}
	};

	const updateApplicationStatus = async (
		newStatus: "pending" | "active" | "suspended",
	) => {
		if (!application) return;

		try {
			setSaving(true);

			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the update-doula-application edge function
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/update-doula-application`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						application_id: application.id,
						updates: {
							profile: {
								status: newStatus,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to update application status");
			}

			if (!result.success) {
				throw new Error(result.error || "Failed to update application status");
			}

			setApplication((prev) =>
				prev
					? {
						...prev,
						status: newStatus,
						updated_at: new Date().toISOString(),
					}
					: null,
			);
		} catch (error) {
			// Handle error silently
		} finally {
			setSaving(false);
		}
	};

	const saveAdminNotes = async () => {
		if (!application) return;

		try {
			setSaving(true);

			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the update-doula-application edge function
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/update-doula-application`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						application_id: application.id,
						updates: {
							profile: {
								admin_notes: adminNotes,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save admin notes");
			}

			if (!result.success) {
				throw new Error(result.error || "Failed to save admin notes");
			}

			setApplication((prev) =>
				prev
					? {
						...prev,
						admin_notes: adminNotes,
						updated_at: new Date().toISOString(),
					}
					: null,
			);
		} catch (error) {
			// Handle error silently
		} finally {
			setSaving(false);
		}
	};

	const updateApplicationField = async (field: string, value: any) => {
		if (!application) return;

		try {
			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the update-doula-application edge function
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/update-doula-application`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						application_id: application.id,
						updates: {
							profile: {
								[field]: value,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || `Failed to update ${field}`);
			}

			if (!result.success) {
				throw new Error(result.error || `Failed to update ${field}`);
			}

			setApplication((prev) =>
				prev
					? {
						...prev,
						[field]: value,
						updated_at: new Date().toISOString(),
					}
					: null,
			);
		} catch (error) {
			// Handle error silently
		}
	};

	const removeArrayItem = async (
		field: "services_offered" | "personality_traits" | "tags",
		itemToRemove: string,
	) => {
		if (!application) return;

		const currentArray = application[field] || [];
		const updatedArray = currentArray.filter((item) => item !== itemToRemove);
		await updateApplicationField(field, updatedArray);
	};

	// Update profile form field and mark as changed
	const updateProfileField = (field: keyof typeof profileForm, value: any) => {
		setProfileForm((prev) => ({
			...prev,
			[field]: value,
		}));
		setHasUnsavedChanges(true);
	};

	// Save all profile changes
	const saveProfileChanges = async () => {
		if (!application || !hasUnsavedChanges) return;

		try {
			setSaving(true);

			// Send each changed field individually
			const promises = Object.entries(profileForm).map(([field, value]) => {
				if (application[field as keyof typeof application] !== value) {
					return updateApplicationField(field, value);
				}
				return Promise.resolve();
			});

			await Promise.all(promises);

			// Update local application state
			setApplication((prev) =>
				prev
					? {
						...prev,
						...profileForm,
					}
					: null,
			);

			setHasUnsavedChanges(false);
		} catch (error) {
			// Handle error silently
		} finally {
			setSaving(false);
		}
	};

	// Separate function for updating packages/experience/certifications
	const updateRelatedData = async (
		dataType: "packages" | "experience" | "certifications",
		updatedItem: any,
	) => {
		if (!application) return;

		try {
			setSaving(true);

			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			const updateData: any = {
				application_id: application.id,
				updates: {},
			};

			// Structure the update based on data type
			if (dataType === "packages") {
				updateData.updates.packages = [
					{
						...updatedItem,
						_action: "update",
					},
				];
			} else if (dataType === "experience") {
				updateData.updates.experience = [
					{
						...updatedItem,
						_action: "update",
					},
				];
			} else if (dataType === "certifications") {
				updateData.updates.certifications = [
					{
						...updatedItem,
						_action: "update",
					},
				];
			}

			// Call the update-doula-application edge function
			const response = await fetch(
				`${(import.meta.env as any).VITE_SUPABASE_URL}/functions/v1/update-doula-application`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify(updateData),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || `Failed to update ${dataType}`);
			}

			if (!result.success) {
				throw new Error(result.error || `Failed to update ${dataType}`);
			}

			// Update local state
			setApplication((prev) => {
				if (!prev) return null;

				const updated = { ...prev };
				if (dataType === "packages") {
					updated.packages =
						prev.packages?.map((item) =>
							item.id === updatedItem.id ? updatedItem : item,
						) || [];
				} else if (dataType === "certifications") {
					updated.certifications =
						prev.certifications?.map((item) =>
							item.id === updatedItem.id ? updatedItem : item,
						) || [];
				}
				updated.updated_at = new Date().toISOString();
				return updated;
			});
		} catch (error) {
			// Handle error silently
		} finally {
			setSaving(false);
		}
	};

	// Video embed helper function
	const getVideoEmbedUrl = (url: string): string | null => {
		if (!url) return null;

		// Clean URL and remove trailing slashes
		const cleanUrl = url.trim().replace(/\/$/, "");

		// YouTube
		if (cleanUrl.includes("youtube.com") || cleanUrl.includes("youtu.be")) {
			let videoId = "";
			if (cleanUrl.includes("youtu.be/")) {
				videoId = cleanUrl.split("youtu.be/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("youtube.com/watch?v=")) {
				videoId = cleanUrl.split("v=")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("youtube.com/embed/")) {
				videoId = cleanUrl.split("embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
		}

		// Vimeo
		if (cleanUrl.includes("vimeo.com")) {
			let videoId = "";
			if (cleanUrl.includes("player.vimeo.com/video/")) {
				videoId = cleanUrl.split("player.vimeo.com/video/")[1].split(/[?&]/)[0];
			} else {
				videoId = cleanUrl.split("vimeo.com/")[1]?.split(/[?&]/)[0];
			}
			return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
		}

		// VEED.io
		if (cleanUrl.includes("veed.io")) {
			let videoId = "";
			if (cleanUrl.includes("veed.io/view/")) {
				videoId = cleanUrl.split("veed.io/view/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("veed.io/embed/")) {
				videoId = cleanUrl.split("veed.io/embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.veed.io/embed/${videoId}` : null;
		}

		// Loom
		if (cleanUrl.includes("loom.com")) {
			let videoId = "";
			if (cleanUrl.includes("loom.com/share/")) {
				videoId = cleanUrl.split("loom.com/share/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("loom.com/embed/")) {
				videoId = cleanUrl.split("loom.com/embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.loom.com/embed/${videoId}` : null;
		}

		// Google Drive
		if (cleanUrl.includes("drive.google.com")) {
			let fileId = "";
			if (cleanUrl.includes("/file/d/")) {
				fileId = cleanUrl.split("/file/d/")[1].split("/")[0];
			} else if (cleanUrl.includes("id=")) {
				fileId = cleanUrl.split("id=")[1].split(/[?&]/)[0];
			}
			return fileId
				? `https://drive.google.com/file/d/${fileId}/preview`
				: null;
		}

		// Wistia
		if (cleanUrl.includes("wistia.com") || cleanUrl.includes("wi.st")) {
			let videoId = "";
			if (cleanUrl.includes("wistia.com/medias/")) {
				videoId = cleanUrl.split("wistia.com/medias/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("wi.st/medias/")) {
				videoId = cleanUrl.split("wi.st/medias/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://fast.wistia.net/embed/iframe/${videoId}` : null;
		}

		// Twitch
		if (cleanUrl.includes("twitch.tv")) {
			let videoId = "";
			if (cleanUrl.includes("twitch.tv/videos/")) {
				videoId = cleanUrl.split("twitch.tv/videos/")[1].split(/[?&]/)[0];
				return `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`;
			} else if (cleanUrl.includes("clips.twitch.tv/")) {
				videoId = cleanUrl.split("clips.twitch.tv/")[1].split(/[?&]/)[0];
				return `https://clips.twitch.tv/embed?clip=${videoId}&parent=${window.location.hostname}`;
			}
		}

		// Dailymotion
		if (cleanUrl.includes("dailymotion.com") || cleanUrl.includes("dai.ly")) {
			let videoId = "";
			if (cleanUrl.includes("dailymotion.com/video/")) {
				videoId = cleanUrl.split("dailymotion.com/video/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("dai.ly/")) {
				videoId = cleanUrl.split("dai.ly/")[1].split(/[?&]/)[0];
			}
			return videoId
				? `https://www.dailymotion.com/embed/video/${videoId}`
				: null;
		}

		// Streamable
		if (cleanUrl.includes("streamable.com")) {
			const videoId = cleanUrl.split("streamable.com/")[1]?.split(/[?&]/)[0];
			return videoId ? `https://streamable.com/e/${videoId}` : null;
		}

		// Facebook Video
		if (cleanUrl.includes("facebook.com") && cleanUrl.includes("/videos/")) {
			const encodedUrl = encodeURIComponent(cleanUrl);
			return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}`;
		}

		// Instagram Video (limited embed support)
		if (
			cleanUrl.includes("instagram.com/p/") ||
			cleanUrl.includes("instagram.com/reel/")
		) {
			const postId = cleanUrl.split(/\/p\/|\/reel\//)[1]?.split(/[?&\/]/)[0];
			return postId ? `https://www.instagram.com/p/${postId}/embed/` : null;
		}

		// If it's already an embed URL, return as is
		if (cleanUrl.includes("embed") || cleanUrl.includes("player")) {
			return cleanUrl;
		}

		// For other formats, return null
		return null;
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "pending":
				return (
					<Badge
						variant="secondary"
						className="bg-yellow-100 text-yellow-800 border-yellow-200"
					>
						<Clock className="w-3 h-3 mr-1" />
						Pending Review
					</Badge>
				);
			case "active":
				return (
					<Badge
						variant="default"
						className="bg-green-100 text-green-800 border-green-200"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Active
					</Badge>
				);
			case "suspended":
				return (
					<Badge
						variant="destructive"
						className="bg-red-100 text-red-800 border-red-200"
					>
						<XCircle className="w-3 h-3 mr-1" />
						Suspended
					</Badge>
				);
			default:
				return <Badge variant="secondary">{status}</Badge>;
		}
	};

	const birthsAttendedOptions = {
		"1-9": "1-9 births",
		"10-19": "10-19 births",
		"20-49": "20-49 births",
		"50-74": "50-74 births",
		"75-99": "75-99 births",
		"100-199": "100-199 births",
		"200-499": "200-499 births",
		"500-1000": "500-1000 births",
		"1000+": "1000+ births",
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading application...</p>
				</div>
			</div>
		);
	}

	if (!application) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<Card className="glass-card border-0 shadow-lg">
					<CardContent className="p-12 text-center">
						<XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
						<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
							Application Not Found
						</h3>
						<p className="text-gray-600 font-karla mb-6">
							The doula application you're looking for doesn't exist or has been
							removed.
						</p>
						<Button
							onClick={() => navigate("/admin-applications")}
							className="bg-primary-500 hover:bg-primary-600 text-white"
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back to Applications
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-4">
							<Button
								variant="ghost"
								onClick={() => navigate("/admin-applications")}
								className="p-2"
							>
								<ArrowLeft className="h-5 w-5" />
							</Button>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 font-karla">
									{application.users.full_name}
								</h1>
								<p className="text-gray-600 font-karla">
									Doula Application Review • Applied{" "}
									{formatDate(application.created_at)}
								</p>
							</div>
						</div>

						<div className="flex items-center space-x-4">
							{getStatusBadge(application.status)}

							<div className="flex space-x-2">
								{application.status === "pending" && (
									<>
										<Button
											onClick={() => updateApplicationStatus("active")}
											disabled={saving}
											className="bg-newTheme-lime text-black font-karla shadow-btn-shadow border-0"
										>
											<CheckCircle className="h-4 w-4 mr-2" />
											Approve
										</Button>
										<Button
											onClick={() => updateApplicationStatus("suspended")}
											disabled={saving}
											variant="destructive"
											className="font-karla border border-newTheme-lightGrey shadow-btn-shadow"
										>
											<XCircle className="h-4 w-4 mr-2" />
											Reject
										</Button>
									</>
								)}

								{application.status === "active" && (
									<Button
										onClick={() => updateApplicationStatus("suspended")}
										disabled={saving}
										variant="destructive"
										className="font-karla border border-newTheme-lightGrey shadow-btn-shadow"
									>
										<Ban className="h-4 w-4 mr-2" />
										Suspend Listing
									</Button>
								)}

								{application.status === "suspended" && (
									<Button
										onClick={() => updateApplicationStatus("active")}
										disabled={saving}
										className="bg-green-600 hover:bg-green-700 text-white font-karla"
									>
										<CheckCircle className="h-4 w-4 mr-2" />
										Restore Listing
									</Button>
								)}
							</div>
						</div>
					</div>
				</motion.div>

				{/* Profile Header Card */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.1 }}
					className="mb-8"
				>
					<Card className="glass-card border-0 shadow-lg">
						<CardContent className="p-6">
							<div className="flex items-start space-x-6">
								<div className="relative">
									<div className="w-24 h-24 rounded-lg overflow-hidden bg-primary-100 flex items-center justify-center">
										{application.profile_photo_url ||
											application.users.avatar_url ? (
											<img
												src={
													application.profile_photo_url ||
													application.users.avatar_url ||
													""
												}
												alt={application.users.full_name}
												className="w-full h-full object-cover"
											/>
										) : (
											<Shield className="h-12 w-12 text-primary-600" />
										)}
									</div>
									{application.is_verified && (
										<div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
											<CheckCircle className="h-3 w-3 text-white" />
										</div>
									)}
								</div>

								<div className="flex-1">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<h3 className="text-xl font-bold text-gray-900 font-karla mb-2">
												{application.title || "Certified Doula"}
											</h3>
											<div className="space-y-2 text-sm text-gray-600">
												<div className="flex items-center">
													<Mail className="h-4 w-4 mr-2 text-primary-600" />
													<span className="font-karla">
														{application.users.email}
													</span>
												</div>
												<div className="flex items-center">
													<MapPin className="h-4 w-4 mr-2 text-primary-600" />
													<span className="font-karla">
														{application.location_city},{" "}
														{application.location_state}
													</span>
												</div>
												<div className="flex items-center">
													<Star className="h-4 w-4 mr-2 text-primary-600" />
													<span className="font-karla">
														{application.years_experience} years experience
													</span>
												</div>
											</div>
										</div>

										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600 font-karla">
													Packages:
												</span>
												<span className="font-semibold text-gray-900">
													{application.packages?.length || 0}
												</span>
											</div>
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600 font-karla">
													Experience:
												</span>
												<span className="font-semibold text-gray-900">
													{application.experience?.length || 0}
												</span>
											</div>
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600 font-karla">
													Certifications:
												</span>
												<span className="font-semibold text-gray-900">
													{application.certifications?.length || 0}
												</span>
											</div>
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600 font-karla">
													Verified:
												</span>
												<Badge
													variant={
														application.is_verified ? "default" : "secondary"
													}
													className={
														application.is_verified
															? "bg-green-100 text-green-800 border-green-200"
															: "bg-gray-100 text-gray-800 border-gray-200"
													}
												>
													{application.is_verified ? "Verified" : "Unverified"}
												</Badge>
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.div>

				{/* Tabs */}
				<Tabs
					defaultValue="profile"
					value={activeTab}
					onValueChange={setActiveTab}
					className="space-y-6"
				>
					<div className="flex justify-center">
						<TabsList className={`${styles.tabButtons} inline-flex border border-newTheme-lightGrey h-auto flex-col lg:flex-row`}>
							<TabsTrigger
								value="profile"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<User className="w-4 h-4" />
								Profile
							</TabsTrigger>
							<TabsTrigger
								value="packages"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Package className="w-4 h-4" />
								Packages
							</TabsTrigger>

							<TabsTrigger
								value="certifications"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Award className="w-4 h-4" />
								Certifications
							</TabsTrigger>
							<TabsTrigger
								value="video"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Play className="w-4 h-4" />
								Intro Video
							</TabsTrigger>
							<TabsTrigger
								value="months"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<CalendarDays className="w-4 h-4" />
								Months Available
							</TabsTrigger>
							<TabsTrigger
								value="admin"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Settings className="w-4 h-4" />
								Admin
							</TabsTrigger>
						</TabsList>
					</div>

					{/* Profile Tab */}
					<TabsContent value="profile" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-8"
						>
							{/* Basic Information */}
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Basic Information
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div>
										<Label htmlFor="title" className="font-karla">
											Professional Title
										</Label>
										<Input
											id="title"
											value={profileForm.title}
											onChange={(e) =>
												updateProfileField("title", e.target.value)
											}
											className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>

									<div>
										<Label htmlFor="bio" className="font-karla">
											Bio
										</Label>
										<Textarea
											id="bio"
											value={profileForm.bio}
											onChange={(e) =>
												updateProfileField("bio", e.target.value)
											}
											rows={4}
											className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<Label htmlFor="years_experience" className="font-karla">
												Years Experience
											</Label>
											<Input
												id="years_experience"
												type="number"
												value={profileForm.years_experience}
												onChange={(e) =>
													updateProfileField(
														"years_experience",
														parseInt(e.target.value) || 0,
													)
												}
												className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
											/>
										</div>

										<div>
											<Label htmlFor="births_attended" className="font-karla">
												Births Attended
											</Label>
											<select
												id="births_attended"
												value={profileForm.births_attended_range}
												onChange={(e) =>
													updateProfileField(
														"births_attended_range",
														e.target.value,
													)
												}
												className="h-auto py-[12px] text-lg bg-white/80 backdrop-blur-sm font-karla border border-newTheme-lightGrey rounded-md p-2 w-full shadow-btn-shadow"
											>
												<option value="">Select range</option>
												{Object.entries(birthsAttendedOptions).map(
													([value, label]) => (
														<option key={value} value={value}>
															{label}
														</option>
													),
												)}
											</select>
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Location & Services */}
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Location & Services
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div>
										<Label htmlFor="address" className="font-karla">
											Address
										</Label>
										<Input
											id="address"
											value={profileForm.location_address}
											onChange={(e) =>
												updateProfileField("location_address", e.target.value)
											}
											className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>

									<div className="grid grid-cols-3 gap-4">
										<div>
											<Label htmlFor="city" className="font-karla">
												City
											</Label>
											<Input
												id="city"
												value={profileForm.location_city}
												onChange={(e) =>
													updateProfileField("location_city", e.target.value)
												}
												className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
											/>
										</div>
										<div>
											<Label htmlFor="state" className="font-karla">
												State
											</Label>
											<Input
												id="state"
												value={profileForm.location_state}
												onChange={(e) =>
													updateProfileField("location_state", e.target.value)
												}
												className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
											/>
										</div>
										<div>
											<Label htmlFor="zip" className="font-karla">
												ZIP Code
											</Label>
											<Input
												id="zip"
												value={profileForm.location_zip}
												onChange={(e) =>
													updateProfileField("location_zip", e.target.value)
												}
												className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
											/>
										</div>
									</div>

									{/* Services Offered */}
									{application.services_offered &&
										application.services_offered.length > 0 && (
											<div>
												<Label className="font-karla">Services Offered</Label>
												<div className="flex flex-wrap gap-2 mt-2">
													{application.services_offered.map(
														(service, index) => (
															<Badge
																key={index}
																variant="secondary"
																className="bg-primary-100 text-primary-800 font-karla pr-1 flex items-center gap-1"
															>
																{service}
																<button
																	onClick={() =>
																		removeArrayItem("services_offered", service)
																	}
																	className="ml-1 hover:bg-primary-200 rounded-full w-4 h-4 flex items-center justify-center text-primary-600 hover:text-primary-800 transition-colors"
																	type="button"
																>
																	×
																</button>
															</Badge>
														),
													)}
												</div>
											</div>
										)}

									{/* Personality Traits */}
									{application.personality_traits &&
										application.personality_traits.length > 0 && (
											<div>
												<Label className="font-karla">Personality Traits</Label>
												<div className="flex flex-wrap gap-2 mt-2">
													{application.personality_traits.map(
														(trait, index) => (
															<Badge
																key={index}
																variant="outline"
																className="bg-sage-100 text-sage-800 border-sage-200 font-karla pr-1 flex items-center gap-1"
															>
																{trait}
																<button
																	onClick={() =>
																		removeArrayItem("personality_traits", trait)
																	}
																	className="ml-1 hover:bg-sage-200 rounded-full w-4 h-4 flex items-center justify-center text-sage-600 hover:text-sage-800 transition-colors"
																	type="button"
																>
																	×
																</button>
															</Badge>
														),
													)}
												</div>
											</div>
										)}

									{/* Tags */}
									{application.tags && application.tags.length > 0 && (
										<div>
											<Label className="font-karla">Specialties</Label>
											<div className="flex flex-wrap gap-2 mt-2">
												{application.tags.map((tag, index) => (
													<Badge
														key={index}
														variant="default"
														className="bg-blue-100 text-blue-800 border-blue-200 font-karla pr-1 flex items-center gap-1"
													>
														{tag}
														<button
															onClick={() => removeArrayItem("tags", tag)}
															className="ml-1 hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-blue-600 hover:text-blue-800 transition-colors"
															type="button"
														>
															×
														</button>
													</Badge>
												))}
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>

						{/* Profile Preferences Section */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Profile Preferences
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-6">
									{/* Additional Specialties */}
									{application.additional_specialties &&
										application.additional_specialties.length > 0 && (
											<div>
												<Label className="font-karla">
													Additional Specialties
												</Label>
												<div className="flex flex-wrap gap-2 mt-2">
													{application.additional_specialties.map(
														(specialty, index) => (
															<Badge
																key={index}
																variant="outline"
																className="bg-green-100 text-green-800 border-green-200 font-karla"
															>
																{specialty.length > 30
																	? specialty.substring(0, 30) + "..."
																	: specialty}
															</Badge>
														),
													)}
												</div>
											</div>
										)}

									{/* Personal Background */}
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										{application.gender && (
											<div>
												<Label className="font-karla">Gender</Label>
												<div className="mt-1 p-2 bg-gray-50 rounded-md">
													<span className="text-gray-900 font-karla">
														{application.gender}
													</span>
												</div>
											</div>
										)}

										{application.race && (
											<div>
												<Label className="font-karla">Race/Ethnicity</Label>
												<div className="mt-1 p-2 bg-gray-50 rounded-md">
													<span className="text-gray-900 font-karla">
														{application.race}
													</span>
												</div>
											</div>
										)}

										{application.religion && (
											<div>
												<Label className="font-karla">Religion</Label>
												<div className="mt-1 p-2 bg-gray-50 rounded-md">
													<span className="text-gray-900 font-karla">
														{application.religion}
													</span>
												</div>
											</div>
										)}
									</div>

									{/* Service Types */}
									{application.doula_service_type &&
										application.doula_service_type.length > 0 && (
											<div>
												<Label className="font-karla">Service Types</Label>
												<div className="flex flex-wrap gap-2 mt-2">
													{application.doula_service_type.map((type, index) => (
														<Badge
															key={index}
															variant="outline"
															className="bg-blue-100 text-blue-800 border-blue-200 font-karla"
														>
															{type}
														</Badge>
													))}
												</div>
											</div>
										)}

									{/* Languages Spoken */}
									{application.languages_spoken &&
										application.languages_spoken.length > 0 && (
											<div>
												<Label className="font-karla">Languages Spoken</Label>
												<div className="flex flex-wrap gap-2 mt-2">
													{application.languages_spoken.map(
														(language, index) => (
															<Badge
																key={index}
																variant="outline"
																className="bg-indigo-100 text-indigo-800 border-indigo-200 font-karla"
															>
																{language}
															</Badge>
														),
													)}
												</div>
											</div>
										)}

									{/* Doula Style */}
									{application.style && application.style.length > 0 && (
										<div>
											<Label className="font-karla">Doula Style</Label>
											<div className="flex flex-wrap gap-2 mt-2">
												{application.style.map((styleItem, index) => (
													<Badge
														key={index}
														variant="outline"
														className="bg-pink-100 text-pink-800 border-pink-200 font-karla"
													>
														{styleItem}
													</Badge>
												))}
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>

						{/* Save Button */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardContent>
									<div className="flex justify-end pt-6 border-t border-gray-200">
										<Button
											onClick={saveProfileChanges}
											disabled={saving || !hasUnsavedChanges}
											className={`font-karla text-black bg-newTheme-lime border border-newTheme-lightGrey shadow-btn-shadow ${hasUnsavedChanges
												? "cursor-auto"
												: "cursor-not-allowed"
												}`}
										>
											<Save className="h-4 w-4 mr-2" />
											{saving
												? "Saving..."
												: hasUnsavedChanges
													? "Save Changes"
													: "No Changes to Save"}
										</Button>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</TabsContent>

					{/* Packages Tab */}
					<TabsContent value="packages" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
						>
							{application.packages && application.packages.length > 0 ? (
								application.packages.map((pkg, index) => (
									<motion.div
										key={pkg.id}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
									>
										<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
											<CardHeader>
												<div className="flex items-start justify-between">
													<div>
														<CardTitle className="text-lg font-karla">
															{pkg.name}
														</CardTitle>
														<div className="flex items-center space-x-2 mt-1">
															<div className="flex flex-col">
																{pkg.discount_percentage > 0 ? (
																	<div>
																		<p className="text-gray-500 line-through font-karla text-sm">
																			{formatCurrency(pkg.price)}
																		</p>
																		<p className="text-primary-600 font-semibold font-karla text-lg">
																			{formatCurrency(
																				pkg.price -
																				(pkg.price *
																					pkg.discount_percentage) /
																				100,
																			)}
																		</p>
																		<div className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-karla mt-1">
																			-{pkg.discount_percentage}% off
																		</div>
																	</div>
																) : (
																	<p className="text-primary-600 font-semibold font-karla text-lg">
																		{formatCurrency(pkg.price)}
																	</p>
																)}
															</div>
															<Badge
																variant={
																	pkg.is_active ? "default" : "secondary"
																}
																className={
																	pkg.is_active
																		? "bg-green-100 text-green-800 border-green-200"
																		: "bg-gray-100 text-gray-800 border-gray-200"
																}
															>
																{pkg.is_active ? "Active" : "Inactive"}
															</Badge>
														</div>
													</div>
													<Button
														variant="outline"
														size="sm"
														onClick={() => {
															setEditingPackage(pkg);
															setEditPackageDialogOpen(true);
														}}
														className="bg-newTheme-sky text-black border-0"
													>
														<Edit className="h-4 w-4" />
													</Button>
												</div>
											</CardHeader>
											<CardContent>
												<p className="text-gray-600 font-karla text-sm leading-relaxed whitespace-pre-line">
													{pkg.description}
												</p>
											</CardContent>
										</Card>
									</motion.div>
								))
							) : (
								<div className="col-span-full">
									<Card className="glass-card border-0 shadow-lg">
										<CardContent className="p-12 text-center">
											<Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
											<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
												No Packages
											</h3>
											<p className="text-gray-600 font-karla">
												This doula hasn't created any service packages yet.
											</p>
										</CardContent>
									</Card>
								</div>
							)}
						</motion.div>
					</TabsContent>

					{/* Certifications Tab */}
					<TabsContent value="certifications" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla flex items-center">
										<Award className="h-5 w-5 mr-2 text-primary-600" />
										Professional Certifications
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-6">
									{/* Existing Certifications */}
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										{application.certifications &&
											application.certifications.length > 0 ? (
											application.certifications.map((cert) => (
												<motion.div
													key={cert.id}
													className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
													whileHover={{ y: -2 }}
												>
													<div className="p-6">
														<div className="flex justify-between items-start mb-4">
															<div className="flex-1">
																<div className="flex items-center mb-2">
																	<Award className="h-5 w-5 text-primary-600 mr-2" />
																	<h4 className="text-lg font-semibold text-gray-900 font-karla">
																		{cert.name}
																	</h4>
																</div>
																<div className="flex items-center text-sm text-gray-500 font-karla mb-3">
																	<Calendar className="h-4 w-4 mr-2" />
																	Issue Date:{" "}
																	{cert.issue_date
																		? formatDate(cert.issue_date)
																		: "N/A"}
																</div>
															</div>
															<div className="flex space-x-2">
																<Button
																	variant="outline"
																	size="sm"
																	onClick={() => {
																		setEditingCertification(cert);
																		setEditCertificationDialogOpen(true);
																	}}
																	className="bg-newTheme-sky text-black border-0"
																>
																	<Edit className="h-4 w-4" />
																</Button>
															</div>
														</div>

														<div className="border-t border-gray-100 pt-4">
															<p className="text-gray-600 font-karla leading-relaxed mb-3 whitespace-pre-line">
																{cert.description || "No description provided"}
															</p>
															{cert.certificate_url && (
																<a
																	href={cert.certificate_url}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="inline-flex items-center text-primary-600 hover:text-primary-700 font-karla text-sm font-medium transition-colors"
																>
																	<Eye className="h-4 w-4 mr-1" />
																	View Certificate
																</a>
															)}
														</div>
													</div>
												</motion.div>
											))
										) : (
											<div className="col-span-full">
												<div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
													<Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
													<p className="text-gray-500 font-karla text-lg">
														No certifications added yet
													</p>
													<p className="text-gray-400 font-karla text-sm mt-2">
														Professional certifications will appear here once
														added
													</p>
												</div>
											</div>
										)}
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</TabsContent>

					{/* Intro Video Tab */}
					<TabsContent value="video" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900 font-karla">
										<Play className="w-6 h-6 text-primary-600" />
										Intro Video
									</CardTitle>
									<CardDescription className="text-gray-600 font-karla">
										Watch the doula's introduction video to get to know them
										better
									</CardDescription>
								</CardHeader>
								<CardContent>
									{application?.intro_video_url ? (
										(() => {
											const embedUrl = getVideoEmbedUrl(
												application.intro_video_url,
											);

											if (embedUrl) {
												return (
													<div className="relative w-full">
														{/* Video Container with Aspect Ratio */}
														<div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-xl shadow-2xl bg-black">
															<iframe
																src={embedUrl}
																title="Doula Intro Video"
																className="absolute top-0 left-0 w-full h-full border-0"
																allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
																allowFullScreen
															/>
														</div>

														{/* Video Info */}
														<div className="mt-4 p-4 bg-gradient-to-r from-sage-50 to-sage-100 rounded-lg border border-sage-200">
															<div className="flex items-center justify-between">
																<div>
																	<h4 className="font-semibold text-gray-900 font-karla">
																		Introduction Video
																	</h4>
																	<p className="text-sm text-gray-600 font-karla">
																		Get to know {application.users.full_name}
																	</p>
																</div>
																<Link
																	to={application.intro_video_url}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="inline-flex items-center px-3 py-2 text-sm font-medium text-black bg-newTheme-lime rounded-lg shadow-btn-shadow hover:bg-primary-50 transition-colors"
																>
																	<Eye className="w-4 h-4 mr-2" />
																	View Original
																</Link>
															</div>
														</div>
													</div>
												);
											} else {
												return (
													<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
														<div className="flex items-center">
															<Play className="w-8 h-8 text-yellow-600 mr-3" />
															<div>
																<h4 className="font-semibold text-yellow-800 font-karla">
																	Video Format Not Supported
																</h4>
																<p className="text-yellow-700 font-karla text-sm mt-1">
																	This video format cannot be embedded. Click
																	below to view in a new tab.
																</p>
															</div>
														</div>
														<div className="mt-4">
															<a
																href={application.intro_video_url}
																target="_blank"
																rel="noopener noreferrer"
																className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
															>
																<Eye className="w-4 h-4 mr-2" />
																Open Video in New Tab
															</a>
														</div>
													</div>
												);
											}
										})()
									) : (
										<div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
											<Play className="h-12 w-12 text-gray-400 mx-auto mb-4" />
											<h3 className="text-lg font-semibold text-gray-900 mb-2 font-karla">
												No Intro Video
											</h3>
											<p className="text-gray-500 font-karla">
												This doula hasn't uploaded an introduction video yet
											</p>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>
					</TabsContent>

					{/* Months Available Tab */}
					<TabsContent value="months" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900 font-karla">
										<CalendarDays className="w-6 h-6 text-newTheme-spruce" />
										Months Available for New Clients
									</CardTitle>
									<CardDescription className="text-gray-600 font-karla">
										Months when this doula is available to take on new clients.
										Families with due dates in unchecked months won't see this
										doula in search results.
									</CardDescription>
								</CardHeader>
								<CardContent>
									{application?.months_available ? (
										<div className="space-y-6">
											{/* Summary */}
											<div className="bg-newTheme-sky rounded-lg p-4 shadow-btn-shadow">
												<div className="flex items-center justify-between mb-3">
													<div>
														<h4 className="font-semibold text-black font-karla">
															Availability Summary
														</h4>
														<p className="text-sm text-black font-karla">
															{
																Object.values(
																	application.months_available,
																).filter(Boolean).length
															}{" "}
															of 12 months selected
														</p>
													</div>
													<div className="text-2xl font-bold text-black">
														{Math.round(
															(Object.values(
																application.months_available,
															).filter(Boolean).length /
																12) *
															100,
														)}
														%
													</div>
												</div>
												{Object.values(application.months_available).filter(
													Boolean,
												).length < 6 && (
														<div className="bg-newTheme-white rounded-lg p-3 mt-3">
															<p className="text-sm text-newTheme-orange font-karla flex items-center">
																<CalendarDays className="h-4 w-4 mr-2" />
																Limited availability (less than 6 months) may
																impact booking opportunities
															</p>
														</div>
													)}
											</div>

											{/* Month Grid */}
											<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
												{getMonthsArray.map(({ key, label, short }) => {
													const isAvailable =
														application.months_available?.[
														key as keyof typeof application.months_available
														];
													return (
														<motion.div
															key={key}
															initial={{ opacity: 0, scale: 0.95 }}
															animate={{ opacity: 1, scale: 1 }}
															transition={{
																duration: 0.3,
																delay:
																	0.1 *
																	[
																		"january",
																		"february",
																		"march",
																		"april",
																		"may",
																		"june",
																		"july",
																		"august",
																		"september",
																		"october",
																		"november",
																		"december",
																	].indexOf(key),
															}}
															className={`relative p-6 rounded-xl border-0 transition-all duration-200 shadow-btn-shadow ${isAvailable
																? "bg-newTheme-sky"
																: "bg-newTheme-cotton cursor-not-allowed"
																}`}
														>
															<div className="text-center">
																<div
																	className={`text-sm font-medium mb-2 ${isAvailable
																		? "text-black"
																		: "text-gray-500"
																		}`}
																>
																	{short}
																</div>
																<div
																	className={`text-xs font-karla ${isAvailable
																		? "text-black"
																		: "text-gray-500"
																		}`}
																>
																	{label}
																</div>
																<div
																	className="mt-3 w-8 h-8 mx-auto rounded-full flex items-center justify-center bg-newTheme-spruce"
																>
																	{isAvailable ? (
																		<CheckCircle className="h-4 w-4 text-white" />
																	) : (
																		<XCircle className="h-4 w-4 text-newTheme-orange" />
																	)}
																</div>
															</div>
															{isAvailable && (
																<div className="absolute top-2 right-2">
																	<div className="w-3 h-3 bg-newTheme-lime rounded-full"></div>
																</div>
															)}
														</motion.div>
													);
												})}
											</div>

											{/* Details */}
											<div className="bg-newTheme-sky rounded-lg p-6">
												<h4 className="font-semibold text-gray-900 font-karla mb-4">
													Availability Details
												</h4>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
													<div>
														<h5 className="font-medium text-green-700 font-karla mb-2 flex items-center">
															<CheckCircle className="h-4 w-4 mr-2" />
															Available Months (
															{
																Object.values(
																	application.months_available,
																).filter(Boolean).length
															}
															)
														</h5>
														<div className="flex flex-wrap gap-2">
															{Object.entries(application.months_available)
																.filter(([_, isAvailable]) => isAvailable)
																.map(([month]) => (
																	<Badge
																		key={month}
																		className="bg-newTheme-lime text-newTheme-spruce border-0 font-karla"
																	>
																		{month.charAt(0).toUpperCase() +
																			month.slice(1)}
																	</Badge>
																))}
														</div>
													</div>
													<div>
														<h5 className="font-medium text-red-700 font-karla mb-2 flex items-center">
															<XCircle className="h-4 w-4 mr-2" />
															Unavailable Months (
															{
																Object.values(
																	application.months_available,
																).filter((available) => !available).length
															}
															)
														</h5>
														<div className="flex flex-wrap gap-2">
															{Object.entries(application.months_available)
																.filter(([_, isAvailable]) => !isAvailable)
																.map(([month]) => (
																	<Badge
																		key={month}
																		variant="outline"
																		className="bg-red-50 text-red-700 border-red-200 font-karla"
																	>
																		{month.charAt(0).toUpperCase() +
																			month.slice(1)}
																	</Badge>
																))}
														</div>
													</div>
												</div>
											</div>

											{/* Impact Assessment */}
											<div className="bg-newTheme-sky rounded-lg p-4">
												<h4 className="font-semibold text-black font-karla mb-2">
													Search Impact
												</h4>
												<p className="text-sm text-black font-karla">
													This doula will appear in search results for parents
													with due dates in{" "}
													<strong>
														{Object.entries(application.months_available)
															.filter(([_, isAvailable]) => isAvailable)
															.map(
																([month]) =>
																	month.charAt(0).toUpperCase() +
																	month.slice(1),
															)
															.join(", ")}
													</strong>
													{Object.values(application.months_available).filter(
														(available) => !available,
													).length > 0 && (
															<span>
																{" "}
																and will be filtered out for parents due in{" "}
																<strong>
																	{Object.entries(application.months_available)
																		.filter(([_, isAvailable]) => !isAvailable)
																		.map(
																			([month]) =>
																				month.charAt(0).toUpperCase() +
																				month.slice(1),
																		)
																		.join(", ")}
																</strong>
															</span>
														)}
													.
												</p>
											</div>
										</div>
									) : (
										<div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
											<CalendarDays className="h-12 w-12 text-gray-400 mx-auto mb-4" />
											<h3 className="text-lg font-semibold text-gray-900 mb-2 font-karla">
												No Months Availability Data
											</h3>
											<p className="text-gray-500 font-karla">
												This doula hasn't set their months availability
												preferences yet
											</p>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>
					</TabsContent>

					{/* Admin Tab */}
					<TabsContent value="admin" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-8"
						>
							{/* Admin Notes */}
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla flex items-center">
										<FileText className="h-5 w-5 mr-2 text-newTheme-orange" />
										Admin Notes
									</CardTitle>
									<CardDescription className="font-karla">
										Internal notes for this doula application (not visible to
										the doula)
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-4">
									<Textarea
										value={adminNotes}
										onChange={(e) => setAdminNotes(e.target.value)}
										placeholder="Add internal notes about this application..."
										rows={6}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
									<Button
										onClick={saveAdminNotes}
										disabled={saving}
										className={`${styles.primaryBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow`}
									>
										<Save className="h-4 w-4 mr-2" />
										{saving ? "Saving..." : "Save Notes"}
									</Button>
								</CardContent>
							</Card>

							{/* Application Info */}
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla flex items-center">
										<Settings className="h-5 w-5 mr-2 text-gray-600" />
										Application Information
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600 font-karla">
												Application ID:
											</span>
											<span className="font-mono text-sm text-gray-900">
												{application.id}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600 font-karla">
												User ID:
											</span>
											<span className="font-mono text-sm text-gray-900">
												{application.user_id}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600 font-karla">
												Applied:
											</span>
											<span className="text-sm text-gray-900 font-karla">
												{formatDate(application.created_at)}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600 font-karla">
												Last Updated:
											</span>
											<span className="text-sm text-gray-900 font-karla">
												{formatDate(application.updated_at)}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-600 font-karla">
												User Since:
											</span>
											<span className="text-sm text-gray-900 font-karla">
												{application.users.created_at
													? formatDate(application.users.created_at)
													: "Unknown"}
											</span>
										</div>
									</div>

									<Separator />

									<div className="space-y-3">
										<h4 className="font-medium text-gray-900 font-karla">
											Quick Stats
										</h4>
										<div className="grid grid-cols-3 gap-4 text-center">
											<div>
												<div className="text-2xl font-bold text-newTheme-orange">
													{application.packages?.length || 0}
												</div>
												<div className="text-xs text-black font-karla">
													Packages
												</div>
											</div>
											<div>
												<div className="text-2xl font-bold text-newTheme-spruce">
													{application.experience?.length || 0}
												</div>
												<div className="text-xs text-black font-karla">
													Experience
												</div>
											</div>
											<div>
												<div className="text-2xl font-bold text-newTheme-lime">
													{application.certifications?.length || 0}
												</div>
												<div className="text-xs text-black font-karla">
													Certifications
												</div>
											</div>
											<div>
												<div className="text-2xl font-bold text-newTheme-grey">
													{application.years_experience}
												</div>
												<div className="text-xs text-black font-karla">
													Years Exp
												</div>
											</div>
											<div>
												<div className="text-2xl font-bold text-newTheme-lightGrey">
													{application.months_available
														? Object.values(
															application.months_available,
														).filter(Boolean).length
														: 12}
												</div>
												<div className="text-xs text-black font-karla">
													Available Months
												</div>
											</div>
											<div>
												<div className="text-2xl font-bold text-newTheme-spruce">
													{application.months_available
														? Math.round(
															(Object.values(
																application.months_available,
															).filter(Boolean).length /
																12) *
															100,
														)
														: 100}
													%
												</div>
												<div className="text-xs text-black font-karla">
													Availability
												</div>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</TabsContent>
				</Tabs>

				{/* Edit Package Modal */}
				<Dialog
					open={editPackageDialogOpen}
					onOpenChange={setEditPackageDialogOpen}
				>
					<DialogContent className="glass-card bg-newTheme-cotton border-0 backdrop-blur-sm">
						<DialogHeader>
							<DialogTitle>Edit Package</DialogTitle>
							<DialogDescription>
								Make changes to the package details below.
							</DialogDescription>
						</DialogHeader>
						{editingPackage && (
							<div className="space-y-4">
								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Package Name
									</Label>
									<Input
										value={editingPackage.name}
										onChange={(e) =>
											setEditingPackage({
												...editingPackage,
												name: e.target.value,
											})
										}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label className="text-sm font-medium text-gray-700 font-karla">
											Price ($)
										</Label>
										<Input
											type="number"
											value={editingPackage.price}
											onChange={(e) =>
												setEditingPackage({
													...editingPackage,
													price: parseFloat(e.target.value),
												})
											}
											className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>
									<div>
										<Label className="text-sm font-medium text-gray-700 font-karla">
											Discount (%)
										</Label>
										<Input
											type="number"
											min="0"
											max="100"
											value={editingPackage.discount_percentage || 0}
											onChange={(e) =>
												setEditingPackage({
													...editingPackage,
													discount_percentage: parseInt(e.target.value) || 0,
												})
											}
											className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>
								</div>

								{/* Price Preview */}
								{editingPackage.discount_percentage > 0 && (
									<div className="bg-sage-50 p-4 rounded-lg border border-sage-200">
										<Label className="text-sm font-medium text-gray-700 font-karla">
											Price Preview
										</Label>
										<div className="mt-2 space-y-1">
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-600 font-karla">
													Original Price:
												</span>
												<span className="text-sm line-through text-gray-500 font-karla">
													{formatCurrency(editingPackage.price)}
												</span>
											</div>
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-600 font-karla">
													Discount ({editingPackage.discount_percentage}%):
												</span>
												<span className="text-sm text-red-600 font-karla">
													-
													{formatCurrency(
														(editingPackage.price *
															editingPackage.discount_percentage) /
														100,
													)}
												</span>
											</div>
											<div className="flex justify-between items-center border-t border-sage-300 pt-2">
												<span className="text-base font-semibold text-gray-900 font-karla">
													Final Price:
												</span>
												<span className="text-lg font-bold text-primary-600 font-karla">
													{formatCurrency(
														editingPackage.price -
														(editingPackage.price *
															editingPackage.discount_percentage) /
														100,
													)}
												</span>
											</div>
										</div>
									</div>
								)}

								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Description
									</Label>
									<Textarea
										value={editingPackage.description}
										onChange={(e) =>
											setEditingPackage({
												...editingPackage,
												description: e.target.value,
											})
										}
										rows={3}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
							</div>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setEditPackageDialogOpen(false)}
								className="bg-white border-newTheme-lightGrey shadow-btn-shadow text-black"
							>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									if (editingPackage) {
										// Update package using the new function for related data
										await updateRelatedData("packages", editingPackage);
										setEditPackageDialogOpen(false);
										setEditingPackage(null);
									}
								}}
								disabled={saving}
								className="bg-newTheme-lime text-black shadow-btn-shadow border-newTheme-lightGrey"
							>
								{saving ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Edit Certification Modal */}
				<Dialog
					open={editCertificationDialogOpen}
					onOpenChange={setEditCertificationDialogOpen}
				>
					<DialogContent className="glass-card bg-newTheme-cotton backdrop-blur-sm border-0">
						<DialogHeader>
							<DialogTitle>Edit Certification</DialogTitle>
							<DialogDescription>
								Update the certification details below.
							</DialogDescription>
						</DialogHeader>
						{editingCertification && (
							<div className="space-y-4">
								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Certification Name
									</Label>
									<Input
										value={editingCertification.name}
										onChange={(e) =>
											setEditingCertification({
												...editingCertification,
												name: e.target.value,
											})
										}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Description
									</Label>
									<Textarea
										value={editingCertification.description || ""}
										onChange={(e) =>
											setEditingCertification({
												...editingCertification,
												description: e.target.value,
											})
										}
										rows={3}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Issue Date
									</Label>
									<Input
										type="date"
										value={editingCertification.issue_date || ""}
										onChange={(e) =>
											setEditingCertification({
												...editingCertification,
												issue_date: e.target.value,
											})
										}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
								<div>
									<Label className="text-sm font-medium text-gray-700 font-karla">
										Certificate URL
									</Label>
									<Input
										type="url"
										value={editingCertification.certificate_url || ""}
										onChange={(e) =>
											setEditingCertification({
												...editingCertification,
												certificate_url: e.target.value,
											})
										}
										className={`${styles.adminInput} text-base h-auto shadow-btn-shadow pl-3 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>
							</div>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setEditCertificationDialogOpen(false)}
								className="bg-white border-newTheme-lightGrey shadow-btn-shadow text-black"
							>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									if (editingCertification) {
										// Update certification using the new function for related data
										await updateRelatedData(
											"certifications",
											editingCertification,
										);
										setEditCertificationDialogOpen(false);
										setEditingCertification(null);
									}
								}}
								disabled={saving}
								className="bg-newTheme-lime text-black shadow-btn-shadow border-newTheme-lightGrey"
							>
								{saving ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
}
